import { useMemo, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { formatCedi, shortId } from "../lib/api";
import { CheckCircle2, FileText, Plus, Printer, Sparkles, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import BeforeAfter from "../components/common/BeforeAfter";
import ConsultationCTA from "../components/common/ConsultationCTA";

const VAT = 0.125;

const DemoHeader = () => (
  <div className="mb-8">
    <div className="text-xs font-semibold uppercase tracking-widest text-primary">Demo 4</div>
    <h1 className="mt-1 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">Quotation & Invoice Automation</h1>
    <p className="mt-3 text-muted-foreground text-lg max-w-3xl">
      Type in what the client needs. The AI drafts a quotation with VAT and discounts in GHS,
      then converts it to an invoice with one click.
    </p>
  </div>
);

const defaultLines = [
  { id: shortId(), desc: "Sample product / service", qty: 1, unit: 0 },
  { id: shortId(), desc: "", qty: 1, unit: 0 },
];

const seedHistory = [
  { id: "Q-1029", client: "Adom Supermarket", total: 12800, status: "Approved", type: "Invoice" },
  { id: "Q-1030", client: "Golden Tulip Kumasi", total: 18400, status: "Pending", type: "Quotation" },
  { id: "Q-1031", client: "Ohene Enterprise", total: 9200, status: "Paid", type: "Invoice" },
];

export default function Demo4Invoice() {
  const [client, setClient] = useState({ name: "Adom Supermarket", email: "finance@adom.com", address: "Spintex Road, Accra" });
  const [lines, setLines] = useState(defaultLines);
  const [discount, setDiscount] = useState(0);
  const [aiPrompt, setAiPrompt] = useState("");
  const [docId] = useState(() => `Q-${Math.floor(1032 + Math.random() * 200)}`);
  const [type, setType] = useState("Quotation");
  const [status, setStatus] = useState("Draft");
  const [history, setHistory] = useState(seedHistory);

  const subtotal = useMemo(() => lines.reduce((s, l) => s + l.qty * l.unit, 0), [lines]);
  const discountValue = subtotal * (discount / 100);
  const taxable = subtotal - discountValue;
  const vat = taxable * VAT;
  const total = taxable + vat;

  const addLine = () => setLines((L) => [...L, { id: shortId(), desc: "", qty: 1, unit: 0 }]);
  const removeLine = (id) => setLines((L) => L.filter((l) => l.id !== id));
  const updateLine = (id, k, v) => setLines((L) => L.map((l) => l.id === id ? { ...l, [k]: k === "desc" ? v : Number(v) || 0 } : l));

  const runAI = () => {
    if (!aiPrompt.trim()) return toast.error("Describe what the client needs.");
    // The AI drafts DESCRIPTIONS and QUANTITIES from the user's brief — the user
    // fills in the unit price. Pricing is never invented by the AI (StaffLink
    // policy: every solution is tailored, so no hardcoded package prices).
    const kws = aiPrompt.toLowerCase();
    const auto = [];
    const push = (desc, qty = 1) => auto.push({ id: shortId(), desc, qty, unit: 0 });
    if (kws.includes("receptionist") || kws.includes("chatbot")) push("AI Receptionist setup + support (tailored)");
    if (kws.includes("crm") || kws.includes("lead")) push("CRM & lead automation subscription", 12);
    if (kws.includes("invoice") || kws.includes("quote")) push("Invoice & quotation automation module");
    if (kws.includes("inventory") || kws.includes("stock")) push("Inventory & sales dashboard");
    if (kws.includes("hr") || kws.includes("onboarding")) push("HR onboarding workflow");
    if (kws.includes("analytics") || kws.includes("dashboard")) push("Executive analytics dashboard");
    if (kws.includes("booking") || kws.includes("appointment")) push("Appointment booking module");
    if (auto.length === 0) push(aiPrompt.trim());
    setLines(auto);
    toast.success(`AI drafted ${auto.length} line item${auto.length > 1 ? "s" : ""} — enter your unit prices to finalise.`);
    setAiPrompt("");
  };

  const convertToInvoice = () => {
    setType("Invoice"); setStatus("Sent");
    setHistory((H) => [{ id: docId, client: client.name, total, status: "Sent", type: "Invoice" }, ...H]);
    toast.success("Converted to invoice and sent.");
  };

  const markPaid = () => { setStatus("Paid"); toast.success("Marked as paid."); };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
      <DemoHeader/>

      <Card className="glass mb-6"><CardContent className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-lg bg-accent/15 text-accent grid place-items-center"><Sparkles className="w-4 h-4"/></div>
          <div>
            <div className="text-xs uppercase tracking-widest text-accent">AI Quote Assistant</div>
            <div className="font-display font-semibold">Describe what the client needs</div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input placeholder="e.g. Client needs a CRM, invoicing and inventory automation"
            value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} data-testid="ai-quote-input"/>
          <Button onClick={runAI} data-testid="ai-quote-run"><Wand2 className="w-4 h-4 mr-2"/> Draft line items</Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          The AI drafts the line items — you set the unit price. Every StaffLink solution is tailored, so we never invent pricing.
        </p>
      </CardContent></Card>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className="glass"><CardContent className="p-6 space-y-4">
            <div className="font-display text-lg font-semibold">Client details</div>
            <div><Label>Client name</Label><Input value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} data-testid="client-name"/></div>
            <div><Label>Email</Label><Input value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} data-testid="client-email"/></div>
            <div><Label>Address</Label><Textarea rows={2} value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} data-testid="client-address"/></div>
            <div><Label>Discount (%)</Label><Input type="number" min={0} max={100} value={discount} onChange={(e) => setDiscount(Number(e.target.value) || 0)} data-testid="discount-pct"/></div>
          </CardContent></Card>

          <Card className="glass"><CardContent className="p-6 space-y-3">
            <div className="font-display text-lg font-semibold">Line items</div>
            {lines.map((l) => (
              <div key={l.id} className="grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-6" placeholder="Description" value={l.desc} onChange={(e) => updateLine(l.id, "desc", e.target.value)} data-testid={`line-desc-${l.id}`}/>
                <Input type="number" className="col-span-2" value={l.qty} onChange={(e) => updateLine(l.id, "qty", e.target.value)} data-testid={`line-qty-${l.id}`}/>
                <Input type="number" className="col-span-3" value={l.unit} onChange={(e) => updateLine(l.id, "unit", e.target.value)} data-testid={`line-unit-${l.id}`}/>
                <Button size="icon" variant="ghost" onClick={() => removeLine(l.id)} className="col-span-1"><Trash2 className="w-4 h-4"/></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addLine} data-testid="add-line"><Plus className="w-4 h-4 mr-1"/> Add line</Button>
          </CardContent></Card>
        </div>

        {/* PDF Preview */}
        <div className="lg:col-span-3">
          <Card className="glass-strong overflow-hidden"><CardContent className="p-0">
            <div className="bg-primary text-primary-foreground px-8 py-6 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest opacity-80">StaffLink Ghana</div>
                <div className="font-display text-2xl">{type} · {docId}</div>
              </div>
              <div className="text-right">
                <Badge className="bg-white/20 hover:bg-white/20 text-white border-none">{status}</Badge>
                <div className="text-xs mt-2 opacity-80">Issued {new Date().toLocaleDateString("en-GB")}</div>
              </div>
            </div>
            <div className="px-8 py-6 space-y-4 bg-background">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-widest">Bill to</div>
                  <div className="font-semibold">{client.name}</div>
                  <div className="text-sm text-muted-foreground whitespace-pre-line">{client.address}</div>
                  <div className="text-sm text-muted-foreground">{client.email}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-widest">From</div>
                  <div className="font-semibold">StaffLink Ghana Ltd</div>
                  <div className="text-sm text-muted-foreground">Accra, Ghana</div>
                  <div className="text-sm text-muted-foreground">hello@stafflinkgh.com</div>
                </div>
              </div>

              <div className="rounded-xl border border-border overflow-hidden">
                <div className="grid grid-cols-12 text-xs uppercase tracking-widest bg-muted/60 px-4 py-2">
                  <div className="col-span-7">Description</div>
                  <div className="col-span-2 text-right">Qty</div>
                  <div className="col-span-3 text-right">Amount</div>
                </div>
                {lines.map((l) => (
                  <div key={l.id} className="grid grid-cols-12 px-4 py-2 text-sm border-t border-border">
                    <div className="col-span-7">{l.desc || <span className="text-muted-foreground italic">—</span>}</div>
                    <div className="col-span-2 text-right">{l.qty}</div>
                    <div className="col-span-3 text-right">{formatCedi(l.qty * l.unit)}</div>
                  </div>
                ))}
              </div>

              <div className="ml-auto max-w-xs space-y-1 text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span>{formatCedi(subtotal)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Discount ({discount}%)</span><span>− {formatCedi(discountValue)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>VAT (12.5%)</span><span>{formatCedi(vat)}</span></div>
                <Separator className="my-2"/>
                <div className="flex justify-between font-display text-lg font-semibold"><span>Total</span><span data-testid="invoice-total">{formatCedi(total)}</span></div>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" onClick={() => window.print()} data-testid="print-btn"><Printer className="w-4 h-4 mr-2"/> Print / Save PDF</Button>
                {type === "Quotation" ? (
                  <Button onClick={convertToInvoice} data-testid="convert-invoice"><FileText className="w-4 h-4 mr-2"/> Convert to invoice</Button>
                ) : (
                  <Button className="bg-accent hover:bg-accent/90 text-accent-foreground" onClick={markPaid} data-testid="mark-paid"><CheckCircle2 className="w-4 h-4 mr-2"/> Mark as paid</Button>
                )}
              </div>
            </div>
          </CardContent></Card>
        </div>
      </div>

      <Card className="glass mt-6"><CardContent className="p-6">
        <div className="font-display text-lg font-semibold mb-3">Quote & invoice history</div>
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="grid grid-cols-6 text-xs uppercase tracking-widest bg-muted/60 px-4 py-2">
            <div>Ref</div><div className="col-span-2">Client</div><div>Type</div><div className="text-right">Total</div><div className="text-right">Status</div>
          </div>
          {history.map((h) => (
            <div key={h.id + h.client} className="grid grid-cols-6 items-center px-4 py-2 text-sm border-t border-border">
              <div className="font-mono text-xs">{h.id}</div>
              <div className="col-span-2">{h.client}</div>
              <div>{h.type}</div>
              <div className="text-right">{formatCedi(h.total)}</div>
              <div className="text-right"><Badge variant={h.status === "Paid" ? "default" : "secondary"}>{h.status}</Badge></div>
            </div>
          ))}
        </div>
      </CardContent></Card>

      <BeforeAfter demoId={4}/>
      <ConsultationCTA demoLabel="Quotation & Invoice"/>
    </div>
  );
}
